import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import countriesData from "../../../../assets/json/countries.json";
import statesData from "../../../../assets/json/states.json";
import statusesData from "../../../../assets/json/statuses.json";
import { O2OPartnerBranchService } from "../../services/o2o-partner-branch.service";
import {
  O2OPartnerBranchAddInterface,
  O2OPartnerBranchEditInterface,
  O2OPartnerBranchListingInterface
} from "../../interfaces/o2o-partner.interface";
import { AlertService } from "../../../share/service/alert.service";

export interface BranchDialogData {
  id: string; // partner id
  branch?: O2OPartnerBranchListingInterface;
}

@Component({
  selector: "app-add-branch-dialog",
  templateUrl: "./add-branch-dialog.component.html",
  styleUrls: ["./add-branch-dialog.component.scss"]
})
export class AddBranchDialogComponent implements OnInit {
  isEditMode = false;
  form = this.fb.group({
    branchName: ["", Validators.required],
    branchAddress: ["", Validators.required],
    postcode: [
      "",
      [Validators.required, Validators.minLength(5), Validators.maxLength(5)]
    ],
    state: ["", Validators.required],
    country: ["Malaysia", Validators.required],
    status: ["", Validators.required],
    isHQ: [false]
  });
  states = statesData;
  countries = countriesData;
  statuses = statusesData;

  constructor(
    private alertService: AlertService,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddBranchDialogComponent>,
    private o2oPartnerBranchService: O2OPartnerBranchService,
    @Inject(MAT_DIALOG_DATA) public data: BranchDialogData
  ) {
    this.isEditMode = !!this.data.branch;
  }

  get isFormChanged(): boolean {
    if (!this.isEditMode) {
      return true;
    }

    const { branch } = this.data;
    const {
      branchName,
      branchAddress,
      postcode,
      state,
      country,
      status,
      isHQ
    } = this.form.value;

    return (
      branchName !== branch.name ||
      branchAddress !== branch.address ||
      postcode !== branch.postcode ||
      state !== branch.state ||
      country !== branch.country ||
      status !== branch.status ||
      isHQ !== branch.isHQ
    );
  }

  ngOnInit(): void {
    if (this.isEditMode) {
      this.form.patchValue({
        branchName: this.data.branch.name,
        branchAddress: this.data.branch.address,
        postcode: this.data.branch.postcode,
        state: this.data.branch.state,
        country: this.data.branch.country,
        status: this.data.branch.status,
        isHQ: this.data.branch.isHQ
      });
    }
  }

  onNext(): void {
    if (!this.form.valid || !this.isFormChanged) {
      return;
    }

    const payload = this.isEditMode
      ? this.getEditPayload()
      : this.getAddPayload();

    const request$ = this.isEditMode
      ? this.o2oPartnerBranchService.update(
          this.data.id,
          payload as O2OPartnerBranchEditInterface
        )
      : this.o2oPartnerBranchService.add(
          this.data.id,
          payload as O2OPartnerBranchAddInterface
        );

    request$.subscribe({
      next: () => {
        const action = this.isEditMode ? "updated" : "added";
        this.alertService.openSnackBar(`Branch ${action} successfully`);
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.alertService.openSnackBar(err.error);
      }
    });
  }

  private getEditPayload(): O2OPartnerBranchEditInterface {
    const {
      branchName,
      branchAddress,
      postcode,
      status,
      country,
      state,
      isHQ
    } = this.form.value;
    const branch = this.data.branch;

    const payload: O2OPartnerBranchEditInterface = {
      id: branch.id,
      status: status
    };

    if (branchName !== branch.name) payload.name = branchName;
    if (branchAddress !== branch.address) payload.address = branchAddress;
    if (postcode !== branch.postcode) payload.postcode = postcode;
    if (state !== branch.state) payload.state = state;
    if (country !== branch.country) payload.country = country;
    if (isHQ !== branch.isHQ) payload.isHQ = isHQ;

    return payload;
  }

  private getAddPayload(): O2OPartnerBranchAddInterface {
    const {
      branchName,
      branchAddress,
      postcode,
      status,
      country,
      state,
      isHQ
    } = this.form.value;

    return {
      name: branchName,
      address: branchAddress,
      postcode,
      state,
      country,
      status,
      isHQ
    };
  }
}
